<script setup>
import axios from "axios";
import { ref, defineProps, computed } from "vue";
import { normalizeProfessional, normalizeLeadership, normalizeEating } from "@/utils/normalize.js";

const props = defineProps({
  roleEnable: {
    type: Boolean,
    default: false
  }
});

const showModal = ref(false);

const handleCsvExport = async () => {
  showModal.value = true;
  try {
    const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/allpersonaldata`, {
      params: {
        withRole: props.roleEnable
      }
    });

    console.log(response.data.data);

    return response.data.data;

  } catch (error) {
    console.error("Fehler beim CSV Export:", error);
  }
};

const items = ref([
  { db: "firstname", name: "Vorname", export: true },
  { db: "name", name: "Name", export: true },
  { db: "email", name: "Email", export: true },
  { db: "birthdate", name: "Geburtstdatum", export: true },
  { db: "birthtown", name: "Geburtsort", export: true },
  { db: "birthcountry", name: "Geburtsland", export: true },
  { db: "birthname", name: "Geburtsname", export: true },
  { db: "unit", name: "Einheit", export: true },
  { db: "eating", name: "Ernährung", export: true },
  { db: "professionalValue", name: "Fachlichqualifikation", export: true },
  { db: "Specialty", name: "Arzt Fachrichtung", export: true },
  { db: "facharzt", name: "Facharzt", export: true },
  { db: "notarztQualifikation", name: "Notarztqualifikation", export: true },
  { db: "leadershipRD", name: "Führung Rettungsdienst", export: true },
  { db: "leadershipKatS", name: "Führung Katastrophenschutz", export: true },
  { db: "bhp", name: "BHP", export: true },
  { db: "ktw", name: "KTW", export: true },
  { db: "zen", name: "Zentrale Dienste", export: true },
  { db: "sonst", name: "Sonstige", export: true },
  { db: "doda", name: "Do. Tag", export: true },
  { db: "doni", name: "Do. Nacht", export: true },
  { db: "doco", name: "Do. Konzert", export: true },
  { db: "frda", name: "Fr. Tag", export: true },
  { db: "frni", name: "Fr. Nacht", export: true },
  { db: "frco", name: "Fr. Konzert", export: true },
  { db: "sada", name: "Sa. Tag", export: true },
  { db: "sani", name: "Sa. Nacht", export: true },
  { db: "saco", name: "Sa. Konzert", export: true },
  { db: "soda", name: "So. Tag", export: true },
  { db: "soni", name: "So. Nacgt", export: true },
  { db: "soco", name: "So. Konzert", export: true },
  { db: "moda", name: "Mo. Tag", export: true }
]);

const chunkedItems = computed(() => {
  const chunks = [];
  for (let i = 0; i < items.value.length; i += 5) {
    chunks.push(items.value.slice(i, i + 5));
  }
  return chunks;
});

const transformData = async () => {
  try {
    // Hole die Daten von der API
    const data = await handleCsvExport();

    // Überprüfen, ob die Daten als Array vorliegen
    if (Array.isArray(data)) {
      // Transformation der Daten
      const transformedData = data.map(item => {
        const result = {}; // Neues Objekt für jedes Element im Array

        // Iteriere durch alle Felder in `items`
        items.value.forEach(field => {
          // Nur, wenn 'export' true ist, wird der Wert in das neue Objekt übernommen
          if (field.export) {
            if (field.db === "leadershipRD" || field.db === "leadershipKatS") {
              result[field.name] = transformLeadershipValue(item[field.db]);
            } else if (field.db === "professionalValue") {
              result[field.name] = transformProfessionalValue(item[field.db]);
            } else if (field.db === "eating") {
              result[field.name] = transformEatingValue(item[field.db]);
            } else if (field.db === "birthdate") {
              result[field.name] = formatDate(item[field.db]);
            } else if (field.db === "facharzt" || field.db === "notarztQualifikation") {
              result[field.name] = item[field.db] ? "ja" : "nein";
            } else {
              result[field.name] = item[field.db];
            }
          }
        });

        return result; // Rückgabe des transformierten Objekts
      });
      return transformedData; // Rückgabe des transformierten Arrays
    } else {
      console.error("Daten sind nicht im erwarteten Array-Format!");
      return [];
    }
  } catch (error) {
    console.error("Fehler beim Datenabruf und der Transformation:", error);
    return [];
  }
};

function formatDate(dateString) {
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Monate starten bei 0
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
}

const transformEatingValue = function(item) {
  switch (normalizeEating(item)) {
    case "vegan": return "Vegan";
    case "vegu": return "Vegan / Glutenfrei";
    case "glutenfrei": return "Glutenfrei";
    case "normal": return "keine Einschränkung";
    default: return "";
  }
}
const transformLeadershipValue = function(item) {
  switch (normalizeLeadership(item)) {
    case "gf":
      return "Gruppenführer";
    case "zf":
      return "Zugführer";
    case "vf":
      return "Verbandsführer";
    case "orgl":
      return "Organisatorischer Leiter RD";
    default:
      return "";
  }
};
const transformProfessionalValue = function(item) {
  switch (normalizeProfessional(item)) {
    case "eh":
      return "Erste Hilfe";
    case "san":
      return "Sanitäter";
    case "rh":
      return "Rettungshelfer";
    case "rs":
      return "Rettungssanitäter";
    case "ra":
      return "Rettungsassistent";
    case "nfs":
      return "Notfallsanitäter";
    case "arzt":
      return "Arzt";
    case "notarzt":
      return "Notarzt";
    default:
      return "";
  }
};

const showModalWindow = function() {
  showModal.value = true;
};

const csvexport = async function() {
  const csvData = await transformData();

  console.log(csvData);

  downloadCsv(csvData);
};

const downloadCsv = (data) => {
  if (data && data.length > 0) {
    // Hole die Keys des ersten Elements für die Kopfzeilen der CSV
    const headers = Object.keys(data[0]);

    // Erstelle den CSV-String
    const csvContent = [
      headers.join(","), // Kopfzeilen
      ...data.map(item => headers.map(header => item[header]).join(","))
    ].join("\n");

    // Erstelle ein Blob mit UTF-8-Kodierung und BOM
    const bom = "\uFEFF"; // BOM (Byte Order Mark) für UTF-8
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });

    // Erstelle einen Link, um die Datei herunterzuladen
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    console.error("Keine Daten zum Herunterladen!");
  }
};
// console.log(transformedData);
</script>

<template>
  <div>
    <el-button @click="showModalWindow">CSV Export</el-button>
  </div>

  <el-dialog title="Daten Export" v-model="showModal">
    <div class="checkbox-container">
      <div class="checkbox-row" v-for="(row, rowIndex) in chunkedItems" :key="rowIndex">
        <div v-for="(item, index) in row" :key="item.db" class="checkbox-item">
          <label>
            <input type="checkbox" v-model="item.export" />
            {{ item.name }}
          </label>
        </div>
      </div>
    </div>


    <template #footer>
      <el-button @click="showModal = false">Schließen</el-button>
      <el-button @click="csvexport">Export</el-button>
    </template>
  </el-dialog>

</template>

<style scoped>
.checkbox-container {
  display: flex;
  flex-direction: column;
  gap: 20px; /* Abstand zwischen den Reihen */
}

.checkbox-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr); /* 5 Spalten */
  gap: 15px; /* Abstand zwischen den Checkboxen */
}

.checkbox-item {
  display: flex;
  flex-direction: column; /* Das Label unter der Checkbox */
  align-items: flex-start; /* Rechtsbündig ausrichten */
  text-align: left; /* Text auch rechts ausrichten */
}
</style>
